import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import type { IntlShape } from 'react-intl';
import { FormattedMessage, useIntl } from 'react-intl';

import type {
  IncomingFormattedError,
  OutgoingFormattedError,
} from '@/errorCodes/common';
import { mapApiErrorCode } from '@/errorCodes/helpers';
import type { Locale } from '@/types/regionTypes';

import { Typography } from '@/components/Typography';
import { useContactInfo } from '@/hooks/useContactInfo';
import type { ApiErrorType, ApiErrorWithType } from '@/lib/types';
import { WarrantyStatusEnum } from '@/views/MyWatchView/types';

import * as S from './ServerErrors.styles';
export interface ServerErrorsProps {
  [key: string]: unknown;
  callCustomerService?: () => void;
  errors: ApiErrorWithType[];
  isFinancingAvailable?: boolean;
  locale?: Locale;
  setErrors?: (errors: ApiErrorWithType[]) => void;
  type?: string;
}

export const getFormattedMessage = (
  error: ApiErrorWithType,
  formatMessage: IntlShape['formatMessage'],
  mapApiErrorCode: (
    type: ApiErrorType,
    error: IncomingFormattedError,
    isFinancingAvailable?: boolean,
    phoneNumber?: string,
  ) => OutgoingFormattedError,
  isFinancingAvailable?: boolean,
  phoneNumber?: string,
) => {
  const mappedMessage = mapApiErrorCode(
    error.type,
    error,
    isFinancingAvailable,
    phoneNumber,
  );

  const message =
    typeof mappedMessage?.message === 'object'
      ? formatMessage(mappedMessage.message, {
          message: error.message || '',
          ...mappedMessage.values,
        })
      : mappedMessage?.message;

  return message;
};

const IncompleteRegistrationNotice = ({
  email,
  errorCode,
}: {
  email: string;
  errorCode: WarrantyStatusEnum.RegisteredByOther | WarrantyStatusEnum.NotFound;
}) => {
  const errorKeyMap = {
    [WarrantyStatusEnum.NotFound]: 'notActivated',
    [WarrantyStatusEnum.RegisteredByOther]: 'registeredByOthers',
  };

  const errorKey = errorKeyMap[errorCode];

  const getMessageId = (suffix: string) =>
    `addWatch.registration.incomplete.${suffix}`;

  return (
    <S.IncompleteRegistrationNotice>
      <p>
        <FormattedMessage id={getMessageId('title')} />
      </p>

      <p>
        <FormattedMessage id={getMessageId(`${errorKey}Intro`)} />
      </p>

      <ul>
        {[1, 2, 3, 4].map((n) => (
          <li key={n}>
            <FormattedMessage id={getMessageId(`item${n}`)} />
          </li>
        ))}
      </ul>

      <p>
        <FormattedMessage
          id={getMessageId('pleaseSend')}
          values={{
            email: (
              <a
                href={`mailto:${email}`}
                rel='noopener noreferrer'
                target='_self'
              >
                {email}
              </a>
            ),
          }}
        />
      </p>

      <p>
        <FormattedMessage id={getMessageId(`${errorKey}Note`)} />
      </p>
    </S.IncompleteRegistrationNotice>
  );
};

export const ServerErrors = ({
  errors,
  type = 'danger',
  ...props
}: ServerErrorsProps) => {
  const [isVisible, setIsVisible] = useState(true);
  const intl = useIntl();
  const contactInfo = useContactInfo();

  useEffect(() => {
    setIsVisible(true);
  }, [errors]);

  if (!isVisible || errors.length === 0) {
    return null;
  }

  const handleClose = () => {
    setIsVisible(false);
    props.setErrors?.([]);
  };

  return (
    <S.Wrapper data-test-id='ServerErrorsWrapper' my={2} type={type} {...props}>
      {errors.map((error, i) => {
        if (
          (error.field === WarrantyStatusEnum.RegisteredByOther?.toString() ||
            error.field === WarrantyStatusEnum.NotFound?.toString()) &&
          error.type === 'addWatch' &&
          contactInfo.email
        ) {
          return (
            <IncompleteRegistrationNotice
              key={error.code}
              email={contactInfo.email}
              errorCode={parseInt(error.field)}
            />
          );
        }
        const message = getFormattedMessage(
          error,
          intl.formatMessage,
          mapApiErrorCode,
          props.isFinancingAvailable,
          contactInfo.phoneNumber,
        );

        if (!message) {
          return null;
        }

        if (error.type === 'adyen' && error.code === 'Refused') {
          return (
            <Typography
              key={error.code + i}
              as='span'
              scale='p3'
              style={{ lineHeight: 2, display: 'inline-block' }}
              textAlign='left'
              width='100%'
            >
              {message}
            </Typography>
          );
        }
        return (
          <Typography
            key={error.code}
            data-test-id={`ServerErrorsTypography-${i}`}
            scale='p3'
          >
            {message}
          </Typography>
        );
      })}
      <S.CloseIconButton onClick={() => handleClose()} />
    </S.Wrapper>
  );
};

export const CustomErrors = ({ message }: { message: ReactNode }) => {
  return message ? (
    <S.CustomWrapper data-test-id='ServerErrorsWrapper' my={2}>
      <S.Badge data-test-id='infoIcon' />
      <Typography scale='p3'>{message}</Typography>
    </S.CustomWrapper>
  ) : null;
};
